import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { fetchCustomerInfoBatch } from "@/lib/my-loads-helpers";
import { queryKeys } from "@/lib/query-keys";


export function usePickups() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch unassigned pickup installations
  const { data: unassignedPickups, refetch: refetchUnassigned } = useQuery({
    queryKey: queryKeys.myLoads.unassignedPickups(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("installations")
        .select(`
          id,
          latitude,
          longitude,
          task_type,
          notes,
          order:installer_orders_view!inner(
            id,
            order_number,
            fulfillment_type,
            status
          ),
          cemetery:cemeteries(name, city, address, latitude, longitude)
        `)
        .is("installer_id", null)
        .is("load_id", null)
        .is("deleted_at", null);

      if (error) throw error;
      
      // Filter for pickup orders with ready_for_installation status
      return data?.filter(inst => 
        inst.order?.fulfillment_type === 'pickup' && 
        (inst.order?.status === 'confirmed' || inst.order?.status === 'ready_for_installation')
      ) || [];
    },
  });

  // Fetch claimed pickups (assigned to installer but not in any load)
  const { data: claimedPickups, refetch: refetchClaimed } = useQuery({
    queryKey: [...queryKeys.myLoads.claimedPickups(), user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("installations")
        .select(`
          id,
          status,
          task_type,
          latitude,
          longitude,
          notes,
          order:installer_orders_view(id, order_number, fulfillment_type),
          cemetery:cemeteries(name, city, address, latitude, longitude)
        `)
        .eq("installer_id", user.id)
        .is("load_id", null)
        .is("deleted_at", null)
        .in("status", ["pending", "in_progress"])
        .eq("task_type", "pickup");

      if (error) throw error;

      // Fetch customer info for each order
      const orderIds = [...new Set(data?.map(inst => inst.order?.id).filter(Boolean) as string[])];
      const customerInfoMap = await fetchCustomerInfoBatch(orderIds);

      // Merge customer info into installations
      return data?.map(inst => ({
        ...inst,
        order: inst.order ? {
          ...inst.order,
          customer: inst.order.id ? customerInfoMap[inst.order.id] || null : null
        } : null
      })) || [];
    },
    enabled: !!user?.id,
    gcTime: 1000 * 60 * 60 * 24,   // 24h offline-tuki
  });

  // Claim installation mutation
  const claimPickupMutation = useMutation({
    mutationFn: async (installationId: string) => {
      const { error } = await supabase
        .from("installations")
        .update({ installer_id: user?.id })
        .eq("id", installationId);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Nouto otettu tehtäväksi!");
      queryClient.invalidateQueries({ queryKey: queryKeys.myLoads.unassignedPickups() });
      queryClient.invalidateQueries({ queryKey: queryKeys.myLoads.claimedPickups() });
      queryClient.invalidateQueries({ queryKey: queryKeys.myLoads.all });
      queryClient.invalidateQueries({ queryKey: ["my-load-installations"] });
    },
    onError: () => {
      toast.error("Virhe: noutoa ei voitu ottaa tehtäväksi");
    },
  });


  // Combined refetch function
  const refetchPickups = async () => {
    await Promise.all([refetchUnassigned(), refetchClaimed()]);
  };

  return {
    unassignedPickups: unassignedPickups || [],
    claimedPickups: claimedPickups || [],
    claimPickup: claimPickupMutation.mutate,
    isClaimingPickup: claimPickupMutation.isPending,
    refetchPickups,
  };
}
